import { Component, FormEvent, ReactNode, Ref } from 'react';
import { Formik, FormikConfig, FormikProps, connect } from 'formik';
import { AnyObjectSchema, InferType, Asserts } from 'yup';

import { DeepPartial } from '../../types';
import BraxFormContext from './context';
import { BraxUncontrolledForm } from '../BraxUncontrolledForm';

export type BraxFormProps<Schema extends AnyObjectSchema> = Pick<FormikConfig<Asserts<Schema>>, 'validateOnChange' | 'validateOnBlur' | 'validateOnMount'> & {
    formikRef?: Ref<FormikProps<Asserts<Schema>>>
    id?: string,
    values: DeepPartial<Asserts<Schema>> | Promise<DeepPartial<Asserts<Schema>>>,
    schema: Schema,
    disabled?: boolean,
    readOnly?: boolean,
    className?: string,
    onBeforeSubmit?: () => void | Promise<void>,
    onSubmit?: (values: InferType<Schema>) => void | Promise<void>,
    onValidationError?: () => void,
    children: ReactNode | ((formik: FormikProps<Asserts<Schema>>) => ReactNode)
};

type BraxFormState = {
    disabled: boolean
};

export class BraxForm<Schema extends AnyObjectSchema> extends Component<BraxFormProps<Schema>, BraxFormState> {

    static defaultProps = {
        validateOnBlur: false,
        values: {}
    };

    state: BraxFormState = {
        disabled: this.props.values instanceof Promise
    };

    formik: FormikProps<Asserts<Schema>> | null = null;

    constructor(props: BraxFormProps<Schema>) {
        super(props);

        if (this.props.values instanceof Promise) {
            const update = (values: DeepPartial<Asserts<Schema>>) => {
                if (this.formik) {
                    this.formik.resetForm({
                        values: this.getValues(this.props.schema.getDefault(), values),
                    });

                    this.setState({ disabled: false });
                } else {
                    setTimeout(() => update(values), 500);
                }
            };

            this.props.values.then(values => update(values));
        }
    }

    setFormik = (formik: FormikProps<Asserts<Schema>> | null) => {
        (window as any).formik = formik;
        this.formik = formik;

        setTimeout(() => {
            if (typeof this.props.formikRef === 'function')
                this.props.formikRef(formik);
            else
                // @ts-ignore
                this.props.formikRef?.current = formik;
        }, 0);
    };

    handleSubmit(event: FormEvent<HTMLFormElement>) {
        this.formik!.handleSubmit(event);

        if (!this.formik!.isValid && this.props.onValidationError)
            this.props.onValidationError();
    }

    async submit() {
        if (this.props.onBeforeSubmit)
            await this.props.onBeforeSubmit();

        // TODO
        await new Promise(r => setTimeout(r, 250))

        if (this.props.onSubmit)
            await this.props.onSubmit(this.props.schema.cast(this.formik!.values));
    }

    getValues(target: any, value: any) {
        if (value instanceof Date || value instanceof RegExp)
            return value;

        const targetIsArray = Array.isArray(target);

        const valueIsArray = Array.isArray(value);

        if (targetIsArray || valueIsArray) {
            if (!targetIsArray)
                target = [];

            if (!valueIsArray)
                value = [];

            if (target.length >= value.length)
                target = target.map((target, index) => this.getValues(target, value[index]));
            else
                target = value.map((value, index) => this.getValues(target[index], value));
        } else {
            const targetIsObject = typeof target === 'object' && target !== null;

            const valueIsObject = typeof value === 'object' && value !== null;

            if (targetIsObject || valueIsObject) {
                if (!targetIsObject)
                    target = {};

                if (valueIsObject)
                    for (const key in value)
                        target[key] = this.getValues(target[key], value[key]);
            } else {
                target = (value === undefined || value === null) && target !== undefined && target !== null ? target : value; 
            }
        }

        return target;
    }

    render() {
        const { children, values, schema, className, onBeforeSubmit, onSubmit, ...props } = this.props;

        return (
            <Formik
                innerRef={formik => this.setFormik(formik)}
                onSubmit={() => this.submit()}
                validationSchema={schema}
                initialValues={values instanceof Promise ? this.props.schema.getDefault() : this.getValues(this.props.schema.getDefault(), values)}
                {...props}
            >
                <BraxFormContext.Provider
                    value={{
                        disabled: this.props.disabled || this.state.disabled,
                        readOnly: this.props.readOnly || false,
                        schema: schema
                    }}
                >
                    <BraxUncontrolledForm className={className} onSubmit={event => this.handleSubmit(event)}>
                        { typeof children === 'function' ? <RenderChildren children={children as any} /> : children }
                    </BraxUncontrolledForm>
                </BraxFormContext.Provider>
            </Formik>
        );
    }

}

const RenderChildren = connect<{ children: (formik: FormikProps<any>) => ReactNode }>(({ formik, children }) => <>{ children(formik) }</>);

export * from './BraxAutocompleteField';
export * from './BraxCheckboxField';
export * from './BraxColorPickerField';
export * from './BraxDateTimeField';
export * from './BraxField';
export * from './BraxFieldGroup';
export * from './BraxFormik';
export * from './BraxNPSField';
export * from './BraxPasswordField';
export * from './BraxPhoneField';
export * from './BraxRadioField';
export * from './BraxRadioToggleButtonField';
export * from './BraxRangeField';
export * from './BraxSearchField';
export * from './BraxSelectField';
export * from './BraxSwitchField';
export * from './BraxTextAreaField';
export * from './BraxTextField';
export * from './BraxUploadBoxField';
